/**
 * Session management utilities
 * Handles user session with localStorage and sessionStorage
 */

import { setCookie, removeCookie } from './cookies';

export interface UserSession {
  userId: string;
  username: string;
  email: string;
  role: string;
  token: string;
  refreshToken?: string;
  expiresAt: number;
}

const SESSION_KEY = 'user_session';
const SESSION_COOKIE = 'session_id';
const REMEMBER_ME_KEY = 'remember_me';

/**
 * Create a new session
 * @param user - User session data
 * @param rememberMe - Whether to persist session across browser sessions
 */
export const createSession = (user: UserSession, rememberMe: boolean = false): void => {
  const sessionData = JSON.stringify(user);
  
  if (rememberMe) {
    // Store in localStorage for persistent session
    localStorage.setItem(SESSION_KEY, sessionData);
    localStorage.setItem(REMEMBER_ME_KEY, 'true');
    
    // Set cookie with 30 days expiration
    setCookie(SESSION_COOKIE, user.userId, {
      expires: 30,
      secure: true,
      sameSite: 'strict'
    });
  } else {
    // Store in sessionStorage for current session only
    sessionStorage.setItem(SESSION_KEY, sessionData);
    localStorage.removeItem(REMEMBER_ME_KEY);
    
    // Set session cookie (expires when browser closes)
    setCookie(SESSION_COOKIE, user.userId, {
      secure: true,
      sameSite: 'strict'
    });
  }
};

/**
 * Get current session
 * @returns User session or null if not found
 */
export const getSession = (): UserSession | null => {
  // Check sessionStorage first (current session)
  let sessionData = sessionStorage.getItem(SESSION_KEY);
  
  // If not in sessionStorage, check localStorage (persistent session)
  if (!sessionData) {
    sessionData = localStorage.getItem(SESSION_KEY);
  }
  
  if (!sessionData) {
    return null;
  }
  
  try {
    const session: UserSession = JSON.parse(sessionData);
    
    // Check if session is expired
    if (session.expiresAt && Date.now() > session.expiresAt) {
      clearSession();
      return null;
    }
    
    return session;
  } catch (error) {
    console.error('Failed to parse session data:', error);
    clearSession();
    return null;
  }
};

/**
 * Update session data
 * @param updates - Partial session data to update
 */
export const updateSession = (updates: Partial<UserSession>): void => {
  const currentSession = getSession();
  
  if (!currentSession) {
    return;
  }
  
  const updatedSession = { ...currentSession, ...updates };
  const sessionData = JSON.stringify(updatedSession);
  const rememberMe = localStorage.getItem(REMEMBER_ME_KEY) === 'true';
  
  if (rememberMe) {
    localStorage.setItem(SESSION_KEY, sessionData);
  } else {
    sessionStorage.setItem(SESSION_KEY, sessionData);
  }
};

/**
 * Clear session
 */
export const clearSession = (): void => {
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(REMEMBER_ME_KEY);
  removeCookie(SESSION_COOKIE);
};

/**
 * Check if user is authenticated
 * @returns True if user has valid session
 */
export const isAuthenticated = (): boolean => {
  const session = getSession();
  return session !== null;
};

/**
 * Get session token
 * @returns Session token or null
 */
export const getSessionToken = (): string | null => {
  const session = getSession();
  return session?.token || null;
};

/**
 * Refresh session expiration
 * @param expiresIn - Expiration time in milliseconds
 */
export const refreshSession = (expiresIn: number = 24 * 60 * 60 * 1000): void => {
  updateSession({
    expiresAt: Date.now() + expiresIn
  });
};

/**
 * Check if session is about to expire
 * @param thresholdMinutes - Minutes before expiration to consider "about to expire"
 * @returns True if session expires within threshold
 */
export const isSessionExpiringSoon = (thresholdMinutes: number = 5): boolean => {
  const session = getSession();
  
  if (!session || !session.expiresAt) {
    return false;
  }
  
  const thresholdMs = thresholdMinutes * 60 * 1000;
  const timeUntilExpiry = session.expiresAt - Date.now();
  
  return timeUntilExpiry > 0 && timeUntilExpiry < thresholdMs;
};
